<template>
    <v-navigation-drawer
        :value="value"
        temporary
        right
        width="46%"
        hide-overlay
        class="drawer-shadow"
        @input="handleDrawerInput">
        <v-card tile class="drawer-content pb-12">
            <v-card-title class="sansation_subtitle primary lighten-2 info--text font-weight-bold py-6 d-flex justify-space-between">
                <div class="d-inline">
                    Update Buyer Status
                </div>
                <div class="d-inline">
                    <v-btn
                        icon
                        outlined
                        color="info"
                        elevation="1"
                        :disabled="statusUpdating"
                        @click="close">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </div>
            </v-card-title>
            <v-card-text class="pt-3">
                <v-container>
                    <v-row>
                        <v-col cols="9" class="px-1 pb-0 align-content-center">
                            <v-select
                                v-model="selectedStatus"
                                :items="statusList"
                                item-text="text"
                                item-value="value"
                                label="New Status"
                                outlined
                                hide-details
                                :disabled="statusUpdating"
                                @change="statusChanged">
                            </v-select>
                        </v-col>
                        <v-col cols="3" class="px-1 align-content-center">
                            <div class="d-flex align-center justify-end">
                                <div class="d-inline mr-1 info--text">Generate Email:</div>
                                <v-checkbox
                                    v-model="generateStatusEmail"
                                    hide-details
                                    color="primary"
                                    :disabled="!statusEmailFound"
                                    class="d-inline ma-0 pa-0 mr-n2">
                                </v-checkbox>
                            </div>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" class="px-1">
                            <div class="info--text mb-2">
                                {{ buyers.length }} buyer{{ buyers.length == 1 ? '' : 's' }} selected for listingID: {{ listingIdentifier }}
                            </div>
                            <v-chip
                                v-for="buyer in buyers"
                                :key="buyer.buyer_id"
                                small
                                outlined
                                class="mr-1 mb-1"
                                :color="previewBuyer && previewBuyer.buyer_id == buyer.buyer_id ? 'primary darken-1' : ''"
                                @click="setPreviewBuyer(buyer)">
                                {{ buyer.buyer_fullname }}
                                <v-icon v-if="emailOverrides[buyer.buyer_id]" x-small right>mdi-pencil</v-icon>
                            </v-chip>
                            <div v-if="buyers.length > 1" class="caption mt-1">
                                Each buyer receives their own email. Click a buyer to preview theirs.
                            </div>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" class="px-1 py-0">
                            <v-progress-linear v-if="loadingStatusPreview || statusUpdating" :indeterminate="true" />
                        </v-col>
                    </v-row>

                    <div v-if="statusEmailPreview"
                        class="mt-6 d-block font-weight-bold d-flex justify-space-between align-center">
                        Email Preview
                        <v-btn
                            small
                            icon
                            @click="showStatusEmailPreview = !showStatusEmailPreview">
                            <v-icon>{{ showStatusEmailPreview ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                        </v-btn>
                    </div>
                    <v-row v-if="statusEmailPreview && showStatusEmailPreview && !loadingStatusPreview" class="mb-1">
                        <v-col cols="12" class="px-1">
                            <EmailPreviewEditor
                                :key="`${previewBuyer ? previewBuyer.buyer_id : ''}-${selectedStatus}`"
                                :subject="statusEmailPreview.mailSubject || ''"
                                :html="statusEmailPreview.mailHtml || ''"
                                :disabled="statusUpdating"
                                :initialOverride="previewBuyer ? emailOverrides[previewBuyer.buyer_id] || null : null"
                                @change="emailEdited" />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" class="d-flex justify-end align-center px-1 mt-2">
                            <v-btn
                                outlined
                                color="primary darken-1"
                                class="mr-3"
                                :disabled="statusUpdating"
                                @click="close">
                                Cancel
                            </v-btn>
                            <v-btn
                                color="primary lighten-2 info--text"
                                :disabled="!selectedStatus || !buyers.length || loadingStatusPreview || statusUpdating"
                                @click="updateStatus">
                                Update Status{{ generateStatusEmail ? ' & Send Email' : '' }}
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>
        </v-card>
    </v-navigation-drawer>
</template>

<script>
    import axios from 'axios';
    import Cookie from 'js-cookie';
    import appUrls from '../../helpers/environment';
    import EmailPreviewEditor from './EmailPreviewEditor.vue';

    /*  SHARED BUYER STATUS DRAWER
        Previews the temailcontent email for the chosen buyer status, then updates the status
        (and optionally sends the email) for one or more buyers on a single listing.

        buyers: [{ buyer_id, email, buyer_fullname, project_id }]
        emits: input (open/closed), updated (server message), failed (error message)
    */
    export default {
        name: 'BuyerStatusDrawer',

        components: {
            EmailPreviewEditor
        },

        props: {
            value: Boolean,
            buyers: { type: Array, default: () => [] },
            statusId: { type: [String, Number], default: null },
            statusList: { type: Array, default: () => [] },
            listingId: String,
            listingIdentifier: String
        },

        data () {
            return {
                selectedStatus: null,
                previewBuyer: null,
                statusUpdating: false,
                loadingStatusPreview: false,
                generateStatusEmail: true,
                statusEmailFound: true,
                showStatusEmailPreview: true,
                statusEmailPreview: null,
                // buyer_id -> { subject, html } for emails the admin edited in the preview
                emailOverrides: {}
            }
        },

        watch: {
            value: {
                immediate: true,
                handler (open) {
                    if ( !open ) return

                    this.selectedStatus = this.statusId
                    this.previewBuyer = this.buyers[0] || null
                    this.getStatusEmailPreview()
                }
            }
        },

        methods: {
            handleDrawerInput (open) {
                // Clicking outside a temporary drawer closes it without going through close()
                if ( !open && this.value ) this.close()
            },

            close () {
                if ( this.statusUpdating ) return

                this.statusEmailPreview = null
                this.emailOverrides = {}
                this.previewBuyer = null
                this.generateStatusEmail = true
                this.statusEmailFound = true
                this.showStatusEmailPreview = true
                this.selectedStatus = null
                this.$emit('input', false)
            },

            // A different status means a different template, so earlier edits no longer apply
            statusChanged () {
                this.emailOverrides = {}
                this.getStatusEmailPreview()
            },

            emailEdited ({ edited, subject, html }) {
                if ( !this.previewBuyer ) return

                if ( edited ) {
                    this.$set(this.emailOverrides, this.previewBuyer.buyer_id, { subject, html })
                } else {
                    this.$delete(this.emailOverrides, this.previewBuyer.buyer_id)
                }
            },

            setPreviewBuyer (buyer) {
                this.previewBuyer = buyer
                this.getStatusEmailPreview()
            },

            getStatusEmailPreview: async function () {
                if ( !this.selectedStatus || !this.previewBuyer ) return

                this.loadingStatusPreview = true
                try {
                    const get_email_preview = await axios.post(`${appUrls.api}/api/admin/buyer_status_email_preview`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        params: {
                            listing_identifier: this.listingIdentifier,
                            status_id: this.selectedStatus,
                            buyer: this.previewBuyer
                        }
                    })

                    // No template in temailcontent = no preview and no email
                    const templateFound = !!(get_email_preview.data.outcome && get_email_preview.data.statusFound)
                    this.statusEmailFound = templateFound
                    this.statusEmailPreview = templateFound ? get_email_preview.data.email_preview : null
                    this.generateStatusEmail = templateFound
                    this.showStatusEmailPreview = templateFound
                } catch ( err ) {
                    console.log(err)
                    this.statusEmailFound = false
                    this.statusEmailPreview = null
                    this.generateStatusEmail = false
                }
                this.loadingStatusPreview = false
            },

            updateStatus: async function () {
                if ( !this.buyers.length || !this.selectedStatus ) return

                this.statusUpdating = true
                try {
                    const update_rs = await axios.post(`${appUrls.api}/api/admin/buyer_status`, {
                        headers: { authentication: Cookie.get('token'), "Content-Type": "application/json" },
                        data: {
                            listing_id: this.listingId,
                            listing_identifier: this.listingIdentifier,
                            selected_buyers: this.buyers,
                            status_id: parseInt(this.selectedStatus),
                            generate_email: this.generateStatusEmail,
                            email_overrides: this.generateStatusEmail ? this.emailOverrides : {}
                        }
                    })

                    this.statusUpdating = false
                    this.close()
                    this.$emit('updated', update_rs.data.message)
                } catch ( err ) {
                    console.log(err)
                    this.statusUpdating = false
                    this.$emit('failed', `Something went wrong trying to update the buyer status for listingID: ${this.listingIdentifier}`)
                }
            }
        }
    }
</script>

<style scoped>
    .drawer-shadow {
        position: fixed !important;
        top: 0 !important;
        right: 0 !important;
        height: 100vh !important;
        z-index: 2000;
        box-shadow: -6px 0 12px rgba(0, 0, 0, 0.15);
    }
    .drawer-content {
        height: 100%; /* Full height of the drawer */
        overflow-y: auto; /* Allow vertical scrolling */
        background-color: whitesmoke !important;
    }
    /* Hide scrollbar but allow scrolling */
    .drawer-content::-webkit-scrollbar {
        display: none; /* For Chrome, Safari, and Edge */
    }
    .drawer-content {
        -ms-overflow-style: none; /* For Internet Explorer and Edge */
        scrollbar-width: none; /* For Firefox */
    }
</style>



// WEBPACK FOOTER //
// src/components/shared/BuyerStatusDrawer.vue